import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield, Search, Radio, AlertTriangle,
  Lock, ArrowRight, Loader2, CheckCircle,
  ChevronRight,
} from 'lucide-react'

// Always use /api — Vite proxies it in dev, nginx proxies it in prod
const API_BASE = '/api'

// ── Scrolling ticker data ──────────────────────────────────────────────────────
const TICKER_ITEMS = [
  { type: 'breach', text: '3,847 credentials from telecom.co leaked 2h ago' },
  { type: 'breach', text: 'Healthcare provider DB exposed — 12,400 records' },
  { type: 'stealer', text: 'Infostealer log: corp.example.com employee compromised' },
  { type: 'breach', text: 'E-commerce platform breach — passwords in plaintext' },
  { type: 'stealer', text: 'Banking credentials found in dark web market' },
  { type: 'breach', text: 'SaaS vendor: 890,000 user emails leaked' },
  { type: 'stealer', text: 'VPN credentials harvested from 2,300 devices' },
  { type: 'breach', text: 'Social platform API breach — tokens exposed' },
  { type: 'breach', text: 'Law firm client data found in ransomware dump' },
  { type: 'stealer', text: 'Crypto wallet seeds harvested via phishing kit' },
]

type Module = 'breach' | 'x' | 'social'
type BreachStep = 'email' | 'otp' | 'results'
type XStep = 'email' | 'otp' | 'username' | 'results'
type SocialStep = 'email' | 'otp' | 'keyword' | 'results'

interface BreachTeaser {
  preview_id: string
  module: string
  // backend field names from _mask_breach_results
  email: string
  breach_count: number
  total_records_exposed: number
  data_types_found: string[]
  breach_names: string[]
}

interface XTeaser {
  preview_id: string
  module: string
  name: string
  username: string
  bio: string
  followers_count: number
  following_count: number
  tweet_count: number
  likes_count: number
  media_count: number
  account_age_years: number
  engagement_score: number
  profile_pic: string
  verified: boolean
  created_at: string
}

interface SocialTeaser {
  preview_id: string
  module: string
  keyword: string
  total_posts: number
  platform_breakdown: Record<string, number>
  top_hashtags: string[]
  top_hashtags_chart: { tag: string; count: number }[]
  sentiment: { positive: number; negative: number; neutral: number }
  sentiment_summary: { positive: number; negative: number; neutral: number }
}

// ── Ticker component ───────────────────────────────────────────────────────────
function LiveTicker() {
  const [offset, setOffset] = useState(0)
  const itemWidth = 340
  const total = TICKER_ITEMS.length

  useEffect(() => {
    const id = setInterval(() => {
      setOffset(p => (p + 1) % total)
    }, 2800)
    return () => clearInterval(id)
  }, [total])

  const visible = [
    TICKER_ITEMS[offset % total],
    TICKER_ITEMS[(offset + 1) % total],
    TICKER_ITEMS[(offset + 2) % total],
  ]

  return (
    <div className="flex items-center gap-2 overflow-hidden">
      <span className="shrink-0 text-[10px] font-bold text-red-400 uppercase tracking-widest bg-red-500/10 border border-red-500/30 rounded px-2 py-0.5">Live</span>
      <div className="flex-1 overflow-hidden">
        <div key={offset} className="flex gap-6 animate-pulse-subtle">
          {visible.map((item, i) => (
            <span key={i} className="shrink-0 text-xs text-gray-400 whitespace-nowrap" style={{ maxWidth: itemWidth }}>
              <span className={item.type === 'stealer' ? 'text-orange-400' : 'text-red-400'}>⚠</span>{' '}
              {item.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Enterprise locked row ──────────────────────────────────────────────────────
function LockedRow({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 border border-gray-800/50 bg-gray-900/20">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-[9px] font-mono text-gray-800 select-none tracking-wider">
          ████████████████████████████████████
        </span>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-4">
        <Lock className="w-2.5 h-2.5 text-gray-700" />
        <span className="text-[9px] font-mono text-gray-700 uppercase tracking-widest">{label}</span>
      </div>
    </div>
  )
}

// ── Data type badge ────────────────────────────────────────────────────────────
function DataTypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    Password: 'bg-red-500/15 text-red-400 border-red-500/30',
    Email: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    Phone: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
    IP: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
    Username: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    Name: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
    Address: 'bg-pink-500/15 text-pink-400 border-pink-500/30',
  }
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded border ${colors[type] || colors.Name}`}>{type}</span>
  )
}

// ── Breach results panel ───────────────────────────────────────────────────────
function BreachResultsPanel({ teaser, previewId }: { teaser: BreachTeaser; previewId: string }) {
  const lockedCount = Math.max(0, teaser.breach_count - 1)
  return (
    <div className="space-y-4">
      {/* Alert header */}
      <div className="flex items-start gap-3 p-4 bg-red-950/40 border border-red-500/40 rounded-xl">
        <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-red-300 mb-0.5">Exposure detected</div>
          <div className="text-xs text-gray-400">
            <span className="text-white font-mono">{teaser.email}</span> was found in{' '}
            <span className="text-red-400 font-bold">{teaser.breach_count} breach {teaser.breach_count === 1 ? 'database' : 'databases'}</span>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">{teaser.breach_count}</div>
          <div className="text-xs text-gray-500">Breaches found</div>
        </div>
        <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-orange-400">{(teaser.total_records_exposed || 0).toLocaleString()}</div>
          <div className="text-xs text-gray-500">Records exposed</div>
        </div>
      </div>

      {/* Data types */}
      {(teaser.data_types_found || []).length > 0 && (
        <div>
          <div className="text-xs text-gray-500 mb-2">Exposed data types</div>
          <div className="flex flex-wrap gap-1.5">
            {(teaser.data_types_found || []).map(t => <DataTypeBadge key={t} type={t} />)}
          </div>
        </div>
      )}

      {/* Locked breach rows */}
      <div className="space-y-2">
        <div className="text-xs text-gray-500 mb-1">Breach sources</div>
        {lockedCount > 0 ? (
          <>
            {Array.from({ length: Math.min(lockedCount, 4) }).map((_, i) => (
              <LockedRow key={i} label="Locked" />
            ))}
            {lockedCount > 4 && (
              <div className="text-center text-xs text-gray-600 py-1">+ {lockedCount - 4} more hidden</div>
            )}
          </>
        ) : (
          <div className="text-xs text-gray-500 text-center py-3">Create a free account to view breach details</div>
        )}
      </div>

      <Link
        to={`/register?preview=${previewId}`}
        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm transition-colors"
      >
        See all {teaser.breach_count} breaches — Create free account
        <ArrowRight className="w-4 h-4" />
      </Link>
      <p className="text-center text-[11px] text-gray-600">Free account · No credit card required</p>
    </div>
  )
}

// ── Stat mini card ─────────────────────────────────────────────────────────────

// ── Deterministic seeded value (for reproducible timeline bars) ───────────────
function seededVal(seed: string, i: number): number {
  let h = 5381
  const s = seed + i
  for (let j = 0; j < s.length; j++) h = ((h << 5) + h) ^ s.charCodeAt(j)
  return (h >>> 0) / 4294967295
}

// ── Tweet activity timeline ────────────────────────────────────────────────────
function TweetTimeline({ username, tweetCount, ageYears }: { username: string; tweetCount: number; ageYears: number }) {
  const MONTHS = ['J','F','M','A','M','J','J','A','S','O','N','D']
  const avg = tweetCount / Math.max(ageYears * 12, 1)
  const raw = MONTHS.map((_, i) => avg * (0.3 + seededVal(username, i) * 1.4))
  const maxVal = Math.max(...raw, 1)
  const pcts = raw.map(v => Math.max(Math.round((v / maxVal) * 100), 4))
  const avgPerMonth = Math.round(avg).toLocaleString()

  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3">
      <div className="flex justify-between items-center mb-2">
        <span className="text-[10px] text-gray-500 uppercase tracking-widest">Tweet Activity</span>
        <span className="text-[10px] text-gray-600">{avgPerMonth}/mo avg</span>
      </div>
      <div className="flex items-end gap-0.5 h-12">
        {pcts.map((pct, i) => (
          <div key={i} className="flex-1 rounded-t-sm" style={{
            height: `${pct}%`,
            background: `rgba(56,189,248,${(0.2 + (pct / 100) * 0.8).toFixed(2)})`,
          }} />
        ))}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[8px] text-gray-700">Jan</span>
        <span className="text-[8px] text-gray-700">Jun</span>
        <span className="text-[8px] text-gray-700">Dec</span>
      </div>
    </div>
  )
}

// ── SVG word cloud ────────────────────────────────────────────────────────────
function WordCloud({ tags }: { tags: { tag: string; count: number }[] }) {
  if (!tags.length) return null
  const max = Math.max(...tags.map(t => t.count), 1)
  const POS: [number, number][] = [
    [100, 35], [40, 20], [163, 20], [60, 58], [148, 58],
    [18, 44], [186, 44], [100, 68], [100, 8],
  ]
  const COLORS = ['#10b981','#38bdf8','#a78bfa','#fb923c','#34d399','#60a5fa','#c084fc','#fbbf24']
  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3">
      <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Keyword Cloud</div>
      <svg viewBox="0 0 200 80" className="w-full h-20">
        {tags.slice(0, 8).map((t, i) => {
          const [x, y] = POS[i] ?? [100, 40]
          const fs = 7 + (t.count / max) * 11
          return (
            <text key={i} x={x} y={y} fontSize={fs} fill={COLORS[i % COLORS.length]}
              opacity={0.45 + (t.count / max) * 0.55}
              textAnchor="middle" dominantBaseline="middle"
              style={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {t.tag}
            </text>
          )
        })}
      </svg>
    </div>
  )
}

// ── SVG donut chart ─────────────────────────────────────────────────────────────
function SentimentDonut({ pos, neg, neu }: { pos: number; neg: number; neu: number }) {
  const r = 36, cx = 44, cy = 44, stroke = 10
  const circ = 2 * Math.PI * r
  const posLen = (pos / 100) * circ
  const negLen = (neg / 100) * circ
  const neuLen = (neu / 100) * circ
  const posOff = 0
  const negOff = -(posLen)
  const neuOff = -(posLen + negLen)

  return (
    <svg viewBox="0 0 88 88" className="w-20 h-20 shrink-0" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1f2937" strokeWidth={stroke} />
      {pos > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#10b981" strokeWidth={stroke}
        strokeDasharray={`${posLen} ${circ - posLen}`} strokeDashoffset={posOff} strokeLinecap="butt" />}
      {neg > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ef4444" strokeWidth={stroke}
        strokeDasharray={`${negLen} ${circ - negLen}`} strokeDashoffset={negOff} strokeLinecap="butt" />}
      {neu > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#4b5563" strokeWidth={stroke}
        strokeDasharray={`${neuLen} ${circ - neuLen}`} strokeDashoffset={neuOff} strokeLinecap="butt" />}
    </svg>
  )
}

// ── X results panel ────────────────────────────────────────────────────────────
function XResultsPanel({ teaser, previewId }: { teaser: XTeaser; previewId: string }) {
  const followers = teaser.followers_count || 0
  const tweets = teaser.tweet_count || 0
  const likes = teaser.likes_count || 0
  const following = teaser.following_count || 0
  const age = teaser.account_age_years || 0
  const engagement = teaser.engagement_score || 0

  const fmt = (n: number) => n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n)

  return (
    <div className="space-y-3">
      {/* Profile card */}
      <div className="bg-gray-900/40 border border-gray-800/60 rounded-sm p-4">
        <div className="flex items-center gap-3 mb-3">
          {teaser.profile_pic ? (
            <img src={teaser.profile_pic} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-sky-500/40" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gray-800 border-2 border-sky-500/40 flex items-center justify-center text-gray-500 font-bold text-xl">
              {teaser.name?.[0] || '?'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-white text-sm">{teaser.name}</span>
              {teaser.verified && <span className="text-sky-400 text-xs">✓</span>}
            </div>
            <div className="text-xs text-gray-500">@{teaser.username}</div>
            {teaser.bio && <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{teaser.bio}</p>}
          </div>
        </div>

        {/* 4-stat grid */}
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {[
            { v: fmt(followers), l: 'Followers', c: 'text-sky-400' },
            { v: fmt(tweets), l: 'Tweets', c: 'text-purple-400' },
            { v: fmt(likes), l: 'Likes', c: 'text-pink-400' },
            { v: fmt(following), l: 'Following', c: 'text-gray-300' },
          ].map(s => (
            <div key={s.l} className="bg-gray-950 border border-gray-800 rounded-lg p-2 text-center">
              <div className={`text-sm font-bold font-mono ${s.c}`}>{s.v}</div>
              <div className="text-[9px] text-gray-600 uppercase tracking-wide mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Account age + engagement bars */}
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-[10px] text-gray-600 mb-1">
              <span>Account age</span><span className="text-gray-400">{age} years</span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${Math.min(100, (age / 15) * 100)}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-[10px] text-gray-600 mb-1">
              <span>Engagement score</span><span className="text-gray-400">{engagement}/100</span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${engagement}%` }} />
            </div>
          </div>
          {/* Follower / following ratio */}
          <div>
            <div className="flex justify-between text-[10px] text-gray-600 mb-1">
              <span>Follower / following ratio</span>
              <span className="text-gray-400">{following > 0 ? (followers / following).toFixed(0) : '∞'}x</span>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-purple-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, following > 0 ? Math.log10(followers / following + 1) * 30 : 100)}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Tweet activity timeline */}
      <TweetTimeline username={teaser.username} tweetCount={tweets} ageYears={age} />

      {/* Locked intel rows */}
      <div className="space-y-1.5">
        <div className="text-[10px] text-gray-600 uppercase tracking-widest mb-1">Intelligence locked</div>
        {['Full tweet history & sentiment timeline', 'Follower network graph', 'Coordinated activity & bot flags', 'Top topics, hashtags & mentions'].map((_item, i) => (
          <LockedRow key={i} label="Locked" />
        ))}
      </div>

      <Link
        to={`/register?preview=${previewId}`}
        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-400 text-black font-semibold text-sm transition-colors"
      >
        Unlock full profile intelligence <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  )
}

// ── Social results panel ───────────────────────────────────────────────────────
function SocialResultsPanel({ teaser, previewId }: { teaser: SocialTeaser; previewId: string }) {
  const total = teaser.total_posts || 0
  const sentiment = teaser.sentiment || teaser.sentiment_summary || { positive: 33, negative: 33, neutral: 34 }
  const pos = Math.round(sentiment.positive ?? 33)
  const neg = Math.round(sentiment.negative ?? 33)
  const neu = Math.round(sentiment.neutral ?? 34)
  const platforms = Object.entries(teaser.platform_breakdown || {})
  const maxPlatform = Math.max(...platforms.map(([, v]) => v), 1)
  const hashtagChart = teaser.top_hashtags_chart || []
  const maxHashtag = Math.max(...hashtagChart.map(h => h.count), 1)

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start gap-3 p-3.5 bg-blue-950/20 border border-blue-500/20 rounded-xl">
        <CheckCircle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-blue-300">{total.toLocaleString()} posts collected</div>
          <div className="text-xs text-gray-500">Keyword: <span className="text-white font-mono">"{teaser.keyword}"</span></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Sentiment donut */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-2">Sentiment</div>
          <div className="flex items-center gap-3">
            <SentimentDonut pos={pos} neg={neg} neu={neu} />
            <div className="space-y-1.5 text-[11px]">
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" /><span className="text-gray-400">{pos}% positive</span></div>
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 shrink-0" /><span className="text-gray-400">{neg}% negative</span></div>
              <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-600 shrink-0" /><span className="text-gray-400">{neu}% neutral</span></div>
            </div>
          </div>
        </div>

        {/* Platform bars */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-2">Platforms</div>
          <div className="space-y-2">
            {platforms.length ? platforms.slice(0, 4).map(([plat, count]) => (
              <div key={plat}>
                <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                  <span className="capitalize">{plat}</span><span className="text-gray-400">{count}</span>
                </div>
                <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(count / maxPlatform) * 100}%` }} />
                </div>
              </div>
            )) : <div className="text-xs text-gray-600">No data</div>}
          </div>
        </div>
      </div>

      {/* Hashtag bar chart + word cloud */}
      {hashtagChart.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-3">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-3">Top Hashtags</div>
            <div className="space-y-2">
              {hashtagChart.map((h, i) => (
                <div key={i}>
                  <div className="flex justify-between text-[11px] mb-0.5">
                    <span className="text-emerald-400 font-mono">{h.tag}</span>
                    <span className="text-gray-600">{h.count}</span>
                  </div>
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500/70 rounded-full" style={{ width: `${(h.count / maxHashtag) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <WordCloud tags={hashtagChart} />
        </div>
      )}

      {/* Locked rows */}
      <div className="space-y-1.5">
        <div className="text-[10px] text-gray-600 uppercase tracking-widest mb-1">Analysis locked</div>
        {['Full post text & author profiles', 'Narrative cluster & topic map', 'Top influencers & reach analysis', 'IOC extraction & STIX export'].map((_, i) => (
          <LockedRow key={i} label="Locked" />
        ))}
      </div>

      <Link
        to={`/register?preview=${previewId}`}
        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm transition-colors"
      >
        View all {total} posts — Create free account <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  )
}

// ── Main widget ────────────────────────────────────────────────────────────────
export default function OsintPreviewWidget() {
  const [module, setModule] = useState<Module>('breach')

  // Breach state
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [breachStep, setBreachStep] = useState<BreachStep>('email')
  const [breachLoading, setBreachLoading] = useState(false)
  const [breachError, setBreachError] = useState('')
  const [breachTeaser, setBreachTeaser] = useState<BreachTeaser | null>(null)

  // X state
  const [xEmail, setXEmail] = useState('')
  const [xOtp, setXOtp] = useState('')
  const [xStep, setXStep] = useState<XStep>('email')
  const [xUsername, setXUsername] = useState('')
  const [xLoading, setXLoading] = useState(false)
  const [xError, setXError] = useState('')
  const [xTeaser, setXTeaser] = useState<XTeaser | null>(null)

  // Social state
  const [socialEmail, setSocialEmail] = useState('')
  const [socialOtp, setSocialOtp] = useState('')
  const [socialStep, setSocialStep] = useState<SocialStep>('email')
  const [keyword, setKeyword] = useState('')
  const [socialLoading, setSocialLoading] = useState(false)
  const [socialError, setSocialError] = useState('')
  const [socialTeaser, setSocialTeaser] = useState<SocialTeaser | null>(null)

  // Reset results when switching module
  useEffect(() => {
    setBreachTeaser(null); setBreachStep('email'); setBreachError('')
    setXTeaser(null); setXStep('email'); setXEmail(''); setXOtp(''); setXError('')
    setSocialTeaser(null); setSocialStep('email'); setSocialEmail(''); setSocialOtp(''); setSocialError('')
  }, [module])

  // ── Breach handlers ──
  const handleBreachRequest = async () => {
    if (!email.trim()) { setBreachError('Enter your email address'); return }
    setBreachLoading(true); setBreachError('')
    try {
      const r = await fetch(`${API_BASE}/preview/breach/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setBreachError(data.detail || 'Failed to send OTP'); return }
      setBreachStep('otp')
    } catch { setBreachError('Network error — please try again') }
    finally { setBreachLoading(false) }
  }

  const handleBreachVerify = async () => {
    if (!otp.trim() || otp.length !== 6) { setBreachError('Enter the 6-digit code'); return }
    setBreachLoading(true); setBreachError('')
    try {
      const r = await fetch(`${API_BASE}/preview/breach/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), otp: otp.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setBreachError(data.detail || 'Invalid or expired code'); return }
      setBreachTeaser(data.teaser); setBreachStep('results')
    } catch { setBreachError('Network error — please try again') }
    finally { setBreachLoading(false) }
  }

  // ── X handlers ──
  const handleXRequestOTP = async () => {
    if (!xEmail.trim()) { setXError('Enter your email address'); return }
    setXLoading(true); setXError('')
    try {
      const r = await fetch(`${API_BASE}/preview/x/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: xEmail.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setXError(data.detail || 'Failed to send OTP'); return }
      setXStep('otp')
    } catch { setXError('Network error — please try again') }
    finally { setXLoading(false) }
  }

  const handleXVerifyOTP = async () => {
    if (!xOtp.trim() || xOtp.length !== 6) { setXError('Enter the 6-digit code'); return }
    setXLoading(true); setXError('')
    try {
      const r = await fetch(`${API_BASE}/preview/x/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: xEmail.trim(), otp: xOtp.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setXError(data.detail || 'Invalid or expired code'); return }
      setXStep('username')
    } catch { setXError('Network error — please try again') }
    finally { setXLoading(false) }
  }

  const handleXLookup = async () => {
    const u = xUsername.replace('@', '').trim()
    if (!u) { setXError('Enter a Twitter/X username'); return }
    setXLoading(true); setXError('')
    try {
      const r = await fetch(`${API_BASE}/preview/x/lookup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: u, email: xEmail.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setXError(data.detail || 'Lookup failed'); return }
      setXTeaser(data.teaser); setXStep('results')
    } catch { setXError('Network error — please try again') }
    finally { setXLoading(false) }
  }

  // ── Social handlers ──
  const handleSocialRequestOTP = async () => {
    if (!socialEmail.trim()) { setSocialError('Enter your email address'); return }
    setSocialLoading(true); setSocialError('')
    try {
      const r = await fetch(`${API_BASE}/preview/social/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: socialEmail.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setSocialError(data.detail || 'Failed to send OTP'); return }
      setSocialStep('otp')
    } catch { setSocialError('Network error — please try again') }
    finally { setSocialLoading(false) }
  }

  const handleSocialVerifyOTP = async () => {
    if (!socialOtp.trim() || socialOtp.length !== 6) { setSocialError('Enter the 6-digit code'); return }
    setSocialLoading(true); setSocialError('')
    try {
      const r = await fetch(`${API_BASE}/preview/social/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: socialEmail.trim(), otp: socialOtp.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setSocialError(data.detail || 'Invalid or expired code'); return }
      setSocialStep('keyword')
    } catch { setSocialError('Network error — please try again') }
    finally { setSocialLoading(false) }
  }

  const handleSocialLookup = async () => {
    if (!keyword.trim()) { setSocialError('Enter a keyword or hashtag'); return }
    setSocialLoading(true); setSocialError('')
    try {
      const r = await fetch(`${API_BASE}/preview/social/lookup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: keyword.trim(), platform: 'twitter', email: socialEmail.trim() })
      })
      const data = await r.json()
      if (!r.ok) { setSocialError(data.detail || 'Search failed'); return }
      setSocialTeaser(data.teaser); setSocialStep('results')
    } catch { setSocialError('Network error — please try again') }
    finally { setSocialLoading(false) }
  }

  const MODULES: { id: Module; icon: React.ReactNode; label: string }[] = [
    { id: 'breach', icon: <Shield className="w-3.5 h-3.5" />, label: 'Breach Intel' },
    { id: 'x', icon: <Search className="w-3.5 h-3.5" />, label: 'X Profile' },
    { id: 'social', icon: <Radio className="w-3.5 h-3.5" />, label: 'Social Listen' },
  ]

  return (
    <div className="w-full max-w-2xl mx-auto">

      {/* Terminal chrome */}
      <div className="bg-[#0d0d14] border border-gray-700/70 border-b-0 rounded-t-lg px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/40" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/40" />
          </div>
          <span className="text-[10px] text-gray-600 font-mono tracking-widest uppercase select-none">SYCEK // Intelligence Terminal</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[9px] text-emerald-600 font-mono uppercase tracking-widest">System Active</span>
        </div>
      </div>

      {/* Flat module tab bar */}
      <div className="bg-[#0a0a10] border-x border-gray-700/70 flex">
        {MODULES.map(m => (
          <button
            key={m.id}
            onClick={() => setModule(m.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[10px] font-bold uppercase tracking-widest border-b-2 transition-all ${
              module === m.id
                ? 'border-emerald-500 text-emerald-400 bg-emerald-500/5'
                : 'border-gray-800 text-gray-600 hover:text-gray-400 hover:bg-gray-900/40'
            }`}
          >
            <span className={module === m.id ? 'text-emerald-400' : 'text-gray-700'}>{m.icon}</span>
            {m.label}
          </button>
        ))}
      </div>

      {/* Input panel */}
      <div className="bg-[#09090f] border border-gray-700/70 border-t-0 rounded-b-lg px-5 py-5">

        {/* ── BREACH ── */}
        {module === 'breach' && (
          <div className="space-y-3">
            {breachStep !== 'results' && (
              <>
                <div className="text-sm text-gray-400 mb-3">
                  {breachStep === 'email'
                    ? 'Enter your email — we\'ll send a verification code to confirm you own it.'
                    : <span>We sent a 6-digit code to <span className="text-white">{email}</span>. Check your inbox.</span>}
                </div>

                {breachStep === 'email' && (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                      <input
                        type="email" value={email} onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleBreachRequest()}
                        placeholder="your@email.com"
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/60"
                      />
                    </div>
                    <button
                      onClick={handleBreachRequest} disabled={breachLoading}
                      className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors"
                    >
                      {breachLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Check <ChevronRight className="w-4 h-4" /></>}
                    </button>
                  </div>
                )}

                {breachStep === 'otp' && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        onKeyDown={e => e.key === 'Enter' && handleBreachVerify()}
                        placeholder="000000" maxLength={6}
                        className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white text-center font-mono tracking-[0.4em] placeholder-gray-700 focus:outline-none focus:border-emerald-500/60"
                      />
                      <button
                        onClick={handleBreachVerify} disabled={breachLoading || otp.length !== 6}
                        className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors"
                      >
                        {breachLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                      </button>
                    </div>
                    <button onClick={() => { setBreachStep('email'); setOtp('') }} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
                      ← Use a different email
                    </button>
                  </div>
                )}

                {breachError && <p className="text-xs text-red-400">{breachError}</p>}
                <p className="text-[11px] text-gray-700">We only check your own email. You verify ownership via OTP.</p>
              </>
            )}

            {breachStep === 'results' && breachTeaser && (
              <BreachResultsPanel teaser={breachTeaser} previewId={breachTeaser.preview_id} />
            )}
          </div>
        )}

        {/* ── X PROFILE ── */}
        {module === 'x' && (
          <div className="space-y-3">
            {xStep === 'email' && (
              <>
                <div className="text-sm text-gray-400 mb-3">Enter your email — we'll send a verification code, then you can look up any public X profile.</div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                      type="email" value={xEmail} onChange={e => setXEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleXRequestOTP()}
                      placeholder="your@email.com"
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-sky-500/60"
                    />
                  </div>
                  <button onClick={handleXRequestOTP} disabled={xLoading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors">
                    {xLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ChevronRight className="w-4 h-4" /></>}
                  </button>
                </div>
                {xError && <p className="text-xs text-red-400">{xError}</p>}
                <p className="text-[11px] text-gray-700">We send the intelligence report to this email after your search.</p>
              </>
            )}

            {xStep === 'otp' && (
              <>
                <div className="text-sm text-gray-400 mb-3">
                  Code sent to <span className="text-white">{xEmail}</span>. Check your inbox.
                </div>
                <div className="flex gap-2">
                  <input
                    type="text" value={xOtp} onChange={e => setXOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={e => e.key === 'Enter' && handleXVerifyOTP()}
                    placeholder="000000" maxLength={6}
                    className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white text-center font-mono tracking-[0.4em] placeholder-gray-700 focus:outline-none focus:border-sky-500/60"
                  />
                  <button onClick={handleXVerifyOTP} disabled={xLoading || xOtp.length !== 6}
                    className="flex items-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors">
                    {xLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                  </button>
                </div>
                <button onClick={() => { setXStep('email'); setXOtp('') }} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
                  ← Use a different email
                </button>
                {xError && <p className="text-xs text-red-400">{xError}</p>}
              </>
            )}

            {xStep === 'username' && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-sky-400 shrink-0" />
                  <span className="text-sm text-gray-400">Email verified. Enter any public X username.</span>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-sm">@</span>
                    <input
                      type="text" value={xUsername} onChange={e => setXUsername(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleXLookup()}
                      placeholder="username" autoFocus
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-7 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-sky-500/60"
                    />
                  </div>
                  <button onClick={handleXLookup} disabled={xLoading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors">
                    {xLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Lookup <ChevronRight className="w-4 h-4" /></>}
                  </button>
                </div>
                {xError && <p className="text-xs text-red-400">{xError}</p>}
              </>
            )}

            {xStep === 'results' && xTeaser && (
              <XResultsPanel teaser={xTeaser} previewId={xTeaser.preview_id} />
            )}
          </div>
        )}

        {/* ── SOCIAL ── */}
        {module === 'social' && (
          <div className="space-y-3">
            {socialStep === 'email' && (
              <>
                <div className="text-sm text-gray-400 mb-3">Enter your email — we'll send a verification code, then search any keyword or hashtag across social media.</div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                      type="email" value={socialEmail} onChange={e => setSocialEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSocialRequestOTP()}
                      placeholder="your@email.com"
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/60"
                    />
                  </div>
                  <button onClick={handleSocialRequestOTP} disabled={socialLoading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors">
                    {socialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ChevronRight className="w-4 h-4" /></>}
                  </button>
                </div>
                {socialError && <p className="text-xs text-red-400">{socialError}</p>}
                <p className="text-[11px] text-gray-700">We send the social intelligence snapshot to this email after your search.</p>
              </>
            )}

            {socialStep === 'otp' && (
              <>
                <div className="text-sm text-gray-400 mb-3">
                  Code sent to <span className="text-white">{socialEmail}</span>. Check your inbox.
                </div>
                <div className="flex gap-2">
                  <input
                    type="text" value={socialOtp} onChange={e => setSocialOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={e => e.key === 'Enter' && handleSocialVerifyOTP()}
                    placeholder="000000" maxLength={6}
                    className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white text-center font-mono tracking-[0.4em] placeholder-gray-700 focus:outline-none focus:border-emerald-500/60"
                  />
                  <button onClick={handleSocialVerifyOTP} disabled={socialLoading || socialOtp.length !== 6}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors">
                    {socialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                  </button>
                </div>
                <button onClick={() => { setSocialStep('email'); setSocialOtp('') }} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
                  ← Use a different email
                </button>
                {socialError && <p className="text-xs text-red-400">{socialError}</p>}
              </>
            )}

            {socialStep === 'keyword' && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="text-sm text-gray-400">Email verified. Search any topic, keyword, or hashtag.</span>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Radio className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                      type="text" value={keyword} onChange={e => setKeyword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSocialLookup()}
                      placeholder="bitcoin, #ukraine, any keyword..." autoFocus
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/60"
                    />
                  </div>
                  <button onClick={handleSocialLookup} disabled={socialLoading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-sm rounded-lg transition-colors">
                    {socialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Search <ChevronRight className="w-4 h-4" /></>}
                  </button>
                </div>
                {socialError && <p className="text-xs text-red-400">{socialError}</p>}
              </>
            )}

            {socialStep === 'results' && socialTeaser && (
              <SocialResultsPanel teaser={socialTeaser} previewId={socialTeaser.preview_id} />
            )}
          </div>
        )}
        {/* Live ticker */}
        <div className="mt-4 pt-3 border-t border-gray-800/40">
          <LiveTicker />
        </div>
      </div>
    </div>
  )
}
